import { classMap } from 'lit-html/directives/class-map.js';
import { nothing } from 'lit-html';
import {
  KatalComponent,
  KatLitElement,
  booleanConverter,
  coerceBooleanClassProperties,
} from '../../shared/base';
import { ifNotNull } from '../../utils/directives';
import { trimmedHtml as html } from '../../shared/trimmed-html';
import {
  formatDateObject,
  isValidDateObject,
  getDateFormatFromLocale,
  createDateFromStringAndFormat,
} from '../../shared/base/date-utils';

/**
 * @component {kat-date-picker} KatalDatepicker Allows a user to select a date and provides a display for the selected date.
 * @example UnitedStatesLocale {"locale": "en-US", "label": "US English", "value": ""}
 * @example Disabled {"locale": "en-US", "label": "Disabled", "value": "", "disabled": "true"}
 * @example GermanLocale {"locale": "de-DE", "label": "German", "value": "01.01.2018"}
 * @example ChineseLocale {"locale": "zh-CN", "label": "Mandarin", "value": "2019/01/01", "script": ""}
 * @example IsoFormat {"locale": "en-US", "label": "Set by ISO String", "value": "2019-09-21T12:00:00.000Z", "script": ""}
 * @example DisabledWeekends {"locale": "en-US", "label": "Disabled Weekends",
 * "script": "
 *   document.querySelector(\"kat-date-picker\").isDateDisabled = function (date) {
 *     return (date.getDay() === 0 || date.getDay() === 6);
 *   };
 * "}
 * @example WithTooltip {"locale": "en-US", "label": "With Tooltip", "tooltip-text": "A tooltip with some additional information", "tooltip-position": "right"}
 * @event {{value: string, isoValue: string, dateObject: Date}} change Change event will fire when either date is updated. See event.detail.value, event.detail.isoValue, and event.detail.dateObject for the selected value
 * @event {{value: string}} invalid Fired when the entered value is not parseable as a Date. The invalid value will be attached to the event as event.detail.value
 * @status Production
 * @theme flo
 */
export class KatalDatePicker extends KatLitElement {
  static get properties() {
    return {
      /**
       * @classprop {string | Date} value Set the value for the date picker. This will only emit an 'invalid' date if it is not well-formed. No 'change' event is
       * emitted if successfully set
       */
      value: { type: String, reflect: true },

      /**
       * @classprop {string | Date} date-value Returns a standard Date object. Setting this attribute/property is exactly the same as setting "value". The
       * "date-value" attribute will not be kept up-to-date automatically.
       */
      dateValue: { type: String },

      /** @katignore*/
      _dateObject: { attribute: false },

      /** @katignore*/
      showing: { attribute: false },

      /**
       * @param {string} locale This is the locale the calendar should be using
       */
      locale: { type: String, reflect: true },

      /**
       * @classprop {string} label Set the Label for the input
       */
      label: { type: String, reflect: true },

      /**
       * @classprop {string} name Set the name attribute for the input
       */
      name: { type: String, reflect: true },

      /**
       * @classprop {boolean} disabled If true the datepicker will be disabled and unable to be interacted with.
       */
      disabled: { type: Boolean, converter: booleanConverter, reflect: true },

      /**
       * @classprop {(date: Date) => boolean} is-date-disabled Sets the function used to determine which dates are disabled. Called for each date rendered in the
       * date picker to determine if the date should be disabled. If this function returns true the given date will be disabled. If the logic of this function is
       * changed the disabled state will only be displayed after the next time the user opens the date picker calendar. If instead you use this setter to set the
       * function again the calendar will be re-rendered immediately with the new logic.
       */
      isDateDisabled: { attribute: false },

      /**
       * @param {boolean} katal-1-2-spacing-fixes A special compatibility flag that when set removes extra margins around this component and sets its height to
       * the correct default. Note that this will become the default behavior in the next major release of Katal. Take a look at our service bulletin page for
       * more details.
       */
      katal12SpacingFixes: {
        type: Boolean,
        attribute: 'katal-1-2-spacing-fixes',
        converter: booleanConverter,
        reflect: true,
      },

      /**
       * @classprop {string} tooltip-text This is the text to be shown inside the tooltip placed next to the label text. The tooltip will only appear if this is
       * set.
       */
      tooltipText: { type: String, attribute: 'tooltip-text' },

      /**
       * @classprop {string} tooltip-position This is the position of the tooltip. Defaults to "top".
       */
      tooltipPosition: { type: String, attribute: 'tooltip-position' },

      /**
       * @classprop {string} tooltip-trigger-icon This is the icon that triggers the tooltip next to the label. Defaults to "help_outline".
       */
      tooltipTriggerIcon: { type: String, attribute: 'tooltip-trigger-icon' },
    };
  }

  constructor() {
    super();
    this.showing = false;
    this.locale = 'en-US';
    this.tooltipPosition = 'top';
    this.tooltipTriggerIcon = 'help_outline';
    this._dateObject = null;
  }

  firstUpdated() {
    this.addEventListener('focusin', this.show);
    this.addEventListener('click', this._onClick);
    this.addEventListener('focusout', this._onFocusOut);
    this.calendar = this.querySelector('kat-calendar');
    this.calendar.isDateDisabled = this.isDateDisabled;
    this.calendar.value = this._dateObject;
  }

  updated(changedProperties) {
    if (changedProperties.has('isDateDisabled')) {
      this.calendar.isDateDisabled = this.isDateDisabled;
    }
    if (changedProperties.has('_dateObject')) {
      this.calendar.value = this._dateObject;
    }
  }

  createRenderRoot() {
    return this;
  }

  set value(value) {
    const oldValue = this.value;
    this.rawValue = value;
    if (
      value === null ||
      value === undefined ||
      !(
        value instanceof Date ||
        typeof value === 'string' ||
        typeof value === 'number'
      )
    ) {
      this._dateObject = null;
      this._value = null;
      return;
    }

    if (value === '') {
      this._dateObject = null;
      this._value = null;
      return;
    }

    if (typeof value === 'string') {
      // Cast to integer if is number
      if (value.match(/^[0-9]+$/)) {
        value = new Date(parseInt(value, 10));
      } else if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/)) {
        value = new Date(value);
      }
    } else if (typeof value === 'number') {
      value = new Date(value);
    }

    let dateObject = value;
    let formattedValue = value;

    // Try converting to format specified by locale
    if (value instanceof Date) {
      formattedValue = formatDateObject(value, this.placeholder);
    } else {
      dateObject = this.createDateFromStringAndPlaceholderFormat(value);
    }

    if (this.isDateDisabled && this.isDateDisabled(dateObject)) {
      const input = this.getElementsByTagName('input')[0];
      input.focus();
      return;
    }

    if (!isValidDateObject(dateObject)) {
      this.emitInvalidEvent(value);
      this._value = null;
      this._dateObject = null;
      return;
    }

    this._dateObject = dateObject;
    this._value = formattedValue;
    this.requestUpdate('value', oldValue);

    // Update the lastValue so the change event fires correctly.
    this.lastValue = this._value;
  }

  get value() {
    return this._value || '';
  }

  get dateValue() {
    return this._dateObject;
  }

  set dateValue(value) {
    this.value = value;
  }

  set locale(locale) {
    const oldLocale = this._locale;
    this._locale = locale;
    if (this.rawValue && !this.value) {
      this.value = this.rawValue;
    } else if (this.value && this.value.length && oldLocale && locale) {
      const oldFormatString = getDateFormatFromLocale(oldLocale);
      const newFormatString = getDateFormatFromLocale(locale);
      const currentDate = createDateFromStringAndFormat(
        this.value,
        oldFormatString
      );
      this.value = formatDateObject(currentDate, newFormatString);
    }
    this.requestUpdate('locale', oldLocale);
  }

  get locale() {
    return this._locale;
  }

  get placeholder() {
    return getDateFormatFromLocale(this.locale);
  }

  show() {
    if (this.showing || this.disabled) {
      return;
    }
    this.showing = true;
  }

  hide() {
    if (this.static || !this.showing) {
      return;
    }
    this.showing = false;
  }

  createDateFromStringAndPlaceholderFormat(dateString) {
    if (!dateString || dateString.length === 0) {
      return new Date();
    }

    return createDateFromStringAndFormat(dateString, this.placeholder);
  }

  // Called when a user interaction changes the selected date
  emitChangeEvent(value) {
    if (value === this.lastValue) {
      return;
    }

    // Setting value sets this.lastValue
    this.value = value;

    const isoValue = this._dateObject ? this._dateObject.toISOString() : '';
    this.dispatchCustomEvent('change', {
      value,
      isoValue,
      dateObject: this._dateObject,
    });
  }

  emitInvalidEvent(value) {
    this.dispatchCustomEvent('invalid', { value });
  }

  render() {
    const showClasses = { show: this.showing };
    const label = this._renderLabel();

    return html`
      <div>
        ${label}
        <kat-input-group
          class=${classMap(showClasses)}
          ?katal-1-2-spacing-fixes=${this.katal12SpacingFixes}
          @change=${this._onInputChange}
        >
          <kat-icon name="calendar-alt"></kat-icon>
          <kat-input
            type="text"
            placeholder=${ifNotNull(this.placeholder)}
            name=${ifNotNull(this.name)}
            value=${ifNotNull(this.value)}
            ?disabled=${this.disabled}
          >
          </kat-input>
        </kat-input-group>
      </div>
      <kat-calendar
        class=${classMap(showClasses)}
        ?katal-1-2-spacing-fixes=${this.katal12SpacingFixes}
        locale=${ifNotNull(this.locale)}
        @hide=${this._onCalendarHide}
        @change=${this._onCalendarChange}
        @focusin=${this._onCalendarFocus}
        @focusout=${this._onCalendarFocusOut}
      >
      </kat-calendar>
    `;
  }

  _renderLabel() {
    const tooltip = this.tooltipText
      ? html`
          <kat-tooltip
            label=${ifNotNull(this.tooltipText)}
            position=${ifNotNull(this.tooltipPosition)}
            trigger-icon=${ifNotNull(this.tooltipTriggerIcon)}
            size="tiny"
          ></kat-tooltip>
        `
      : nothing;

    const label = this.label
      ? html`
          ${this.label}&nbsp
        `
      : nothing;
    return html`
      <label class="date-label">${label}${tooltip}</label>
    `;
  }

  _onClick() {
    this.querySelector('kat-input input').focus();
    this.show();
  }

  _onInputChange(e) {
    e.stopImmediatePropagation();
    this.emitChangeEvent(e.detail.value);
  }

  _onFocusOut(e) {
    e.stopImmediatePropagation();
    const target = e.target;
    const relatedTarget = e.relatedTarget;

    const targetIsInsideComponent = target && this.contains(target);
    const relatedTargetInsideComponent =
      relatedTarget && this.contains(relatedTarget);

    if (targetIsInsideComponent && !relatedTargetInsideComponent) {
      this.hide();
    }
  }

  _onCalendarChange(e) {
    e.stopImmediatePropagation();

    // Receive the date event and render it using the date format
    const formattedDate = formatDateObject(e.detail.date, this.placeholder);

    this.emitChangeEvent(formattedDate);
    this.hide();
  }

  _onCalendarHide(e) {
    e.stopImmediatePropagation();
    this.hide();
  }

  _onCalendarFocus(e) {
    e.stopImmediatePropagation();
  }

  _onCalendarFocusOut(e) {
    e.stopImmediatePropagation();
    const calendar = this.querySelector('kat-calendar');
    const relatedTarget = e.relatedTarget;

    if (!calendar.contains(relatedTarget) && !calendar.keyPressFocusSwitch) {
      this.hide();
    }
    calendar.keyPressFocusSwitch = false;
  }
}

coerceBooleanClassProperties(KatalDatePicker);
KatalComponent.register('kat-date-picker', KatalDatePicker);
